/**
 * TanStack Query hooks — Public Intake (Chunk 9.N.2).
 *
 * Routes API consommées :
 *   GET  /v1/public/intake/:token             → metadata 9.N.1.B
 *   POST /v1/public/intake/:token (multipart) → submission 9.N.1.B2
 *
 * Spécificité vs autres queries/* :
 *   - **No auth** — utilise `apiPublic.ts` (pas `api.ts`).
 *   - **Anti-leak unique** côté wire : le helper backend retourne 200
 *     ok:false `PUBLIC_INTAKE_LINK_UNAVAILABLE` pour TOUTE défaillance
 *     token-level. Le hook expose `.data` typed avec `status:"valid"|"invalid"`
 *     pour que la page dispatche proprement sans exposer la `reason` réelle.
 *   - **Pas de retry sur 4xx** — le rate-limit 429 + le 415/400 sont
 *     déterministes côté serveur, retry serait contre-productif.
 */

import type { PublicIntakeMetadata, PublicIntakePostSuccess } from "@kollectivia/shared";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  publicGet,
  publicPostJson,
  publicPostMultipart,
  type PublicFetchResult,
} from "../apiPublic";

// ---------------------------------------------------------------------------
// GET metadata — useQuery
// ---------------------------------------------------------------------------

/**
 * Représentation FE de la réponse GET metadata.
 *
 *   - `valid` : token accepté + lien actif → render le formulaire.
 *   - `invalid` : token rejeté (anti-leak unique) → render `<InvalidState>`.
 *   - `error` : erreur réseau ou shape inattendue → fallback `<InvalidState>`
 *     (parité anti-leak — l'utilisateur final ne distingue pas "lien pourri"
 *     de "API down").
 */
export type IntakeMetadataState =
  | { readonly kind: "valid"; readonly metadata: PublicIntakeMetadata }
  | {
      // 9.U.5 — lien versionné : le GET expose UNIQUEMENT kind/language/schema
      // (zéro titre projet / tenant / métadonnée interne, cf. cadrage F2 §9.U.5).
      readonly kind: "form";
      readonly language: "fr" | "en";
      readonly schema: Record<string, unknown>;
      /**
       * 9.BJ.3 — valeurs SUGGÉRÉES par le serveur pour les champs qui déclarent
       * `prefill_from`. Une carte plate, jamais une structure métier : le web
       * reçoit des valeurs, jamais des sources, et n'a donc aucune règle à
       * rejouer.
       */
      readonly prefill: Readonly<Record<string, string>>;
    }
  | { readonly kind: "invalid" }
  | { readonly kind: "error" };

/** Shape brute du GET versionné (9.U.5) — distincte de PublicIntakeMetadata. */
type VersionedGetData = {
  readonly kind?: unknown;
  readonly language?: unknown;
  readonly schema?: unknown;
  readonly status?: unknown;
  readonly prefill?: unknown;
};

function buildPath(token: string, language?: "fr" | "en"): string {
  // Encode le token (URL-safe mais on chaîne via encodeURIComponent par sécurité —
  // le token est composé de [base64url + dots], donc déjà safe, mais defensif.)
  return `/v1/public/intake/${encodeURIComponent(token)}${language ? `?lang=${language}` : ""}`;
}

export function usePublicIntakeMetadata(token: string | undefined, language?: "fr" | "en") {
  return useQuery<IntakeMetadataState>({
    queryKey: ["public-intake", "metadata", token, language],
    // Keep the mounted form and its input while only the language is refetched.
    // Never carry metadata across a different magic link.
    placeholderData: (previous, query) => (query?.queryKey[2] === token ? previous : undefined),
    queryFn: async () => {
      if (!token) return { kind: "invalid" } as const;
      const result = await publicGet<PublicIntakeMetadata & VersionedGetData>(
        buildPath(token, language),
      );
      // Cas 1 — legacy : succès 200 + envelope ok:true + status:"valid" → metadata
      if (result.envelope.ok && result.envelope.data?.status === "valid") {
        return { kind: "valid", metadata: result.envelope.data } as const;
      }
      // Cas 1bis — versionné 9.U.5 : ok:true + kind:"form" + language + schema.
      if (
        result.envelope.ok &&
        result.envelope.data?.kind === "form" &&
        (result.envelope.data.language === "fr" || result.envelope.data.language === "en") &&
        typeof result.envelope.data.schema === "object" &&
        result.envelope.data.schema !== null
      ) {
        // 9.BJ.3 — on ne garde que les entrées CHAÎNE. Une carte malformée ne
        // doit pas empêcher le formulaire de s'ouvrir : le client peut toujours
        // saisir, il aura simplement à tout écrire.
        const brut = result.envelope.data.prefill;
        const prefill: Record<string, string> = {};
        if (brut && typeof brut === "object" && !Array.isArray(brut)) {
          for (const [k, v] of Object.entries(brut as Record<string, unknown>)) {
            if (typeof v === "string" && v.length > 0) prefill[k] = v;
          }
        }
        return {
          kind: "form",
          language: result.envelope.data.language,
          schema: result.envelope.data.schema as Record<string, unknown>,
          prefill,
        } as const;
      }
      // Cas 2 — anti-leak (200 ok:false PUBLIC_INTAKE_LINK_UNAVAILABLE OU 429
      //         RATE_LIMITED OU autre 4xx structuré) → page invalid uniforme
      return { kind: "invalid" } as const;
    },
    enabled: !!token,
    retry: (failureCount, err) => {
      // Pas de retry sur PublicNetworkError (offline / non-JSON) — l'utilisateur
      // verra `<InvalidState>` immédiatement. Et pas de retry sur 4xx (déjà
      // mappé en kind:"invalid" via queryFn — le throw n'arrivera pas ici).
      return failureCount < 1 && !(err instanceof Error);
    },
  });
}

// ---------------------------------------------------------------------------
// POST submission — useMutation
// ---------------------------------------------------------------------------

/**
 * Result mutation typé pour permettre au form de dispatcher selon kind :
 *   - `received` : succès 201 + intake_event_id opaque → SuccessState
 *   - `invalid` : 200 ok:false PUBLIC_INTAKE_LINK_UNAVAILABLE (token-level
 *     ou honeypot ou race quota) → page invalide
 *   - `payload` : 400 PUBLIC_INTAKE_INVALID_PAYLOAD (consent KO etc.) →
 *     toast user-friendly
 *   - `file_rejected` : 415 PUBLIC_INTAKE_FILE_REJECTED (multipart KO) →
 *     toast + reset form fichiers
 *   - `rate_limited` : 429 RATE_LIMITED → toast + retry-after seconds
 *   - `internal` : 500 PUBLIC_INTAKE_INTERNAL_ERROR ou réseau →
 *     toast générique "réessayer plus tard"
 */
export type IntakeSubmissionResult =
  | { readonly kind: "received"; readonly intakeEventId: string }
  | { readonly kind: "invalid" }
  | { readonly kind: "payload" }
  | { readonly kind: "file_rejected" }
  | { readonly kind: "rate_limited"; readonly retryAfterSec: number | null }
  | { readonly kind: "internal" };

function dispatchSubmissionResult(
  result: PublicFetchResult<PublicIntakePostSuccess>,
): IntakeSubmissionResult {
  // 201 happy
  if (result.status === 201 && result.envelope.ok && result.envelope.data?.status === "received") {
    return { kind: "received", intakeEventId: result.envelope.data.intake_event_id };
  }
  // ok:false envelope dispatch par status + code
  if (!result.envelope.ok) {
    const code = result.envelope.error.code;
    if (result.status === 200 || code === "PUBLIC_INTAKE_LINK_UNAVAILABLE") {
      return { kind: "invalid" };
    }
    if (result.status === 400 || code === "PUBLIC_INTAKE_INVALID_PAYLOAD") {
      return { kind: "payload" };
    }
    if (result.status === 415 || code === "PUBLIC_INTAKE_FILE_REJECTED") {
      return { kind: "file_rejected" };
    }
    if (result.status === 429 || code === "RATE_LIMITED") {
      return { kind: "rate_limited", retryAfterSec: result.retryAfter };
    }
  }
  return { kind: "internal" };
}

export function useSubmitPublicIntake(token: string | undefined) {
  return useMutation<IntakeSubmissionResult, Error, FormData>({
    mutationFn: async (formData: FormData) => {
      if (!token) return { kind: "invalid" } as const;
      try {
        const result = await publicPostMultipart<PublicIntakePostSuccess>(
          buildPath(token),
          formData,
        );
        return dispatchSubmissionResult(result);
      } catch {
        // Network error → fallback uniforme internal (anti-leak — pas de
        // détail du réseau exposé à l'utilisateur).
        return { kind: "internal" };
      }
    },
    retry: false, // déterministe — pas de retry auto sur 4xx/5xx publics
  });
}

// ---------------------------------------------------------------------------
// POST submission VERSIONNÉE (JSON) — 9.U.5 magic-link versionné
// ---------------------------------------------------------------------------

/**
 * Corps du POST versionné. `submission_id` est la clé d'idempotence : le caller
 * la génère UNE fois par montage (ref stable) → un retry réseau réémet la même
 * clé → réponse identique côté serveur (created→replay), quota inchangé.
 */
export type VersionedSubmitBody = {
  readonly data: Record<string, unknown>;
  readonly consent: { readonly accepted: true; readonly timestamp: string };
  readonly submission_id: string;
  readonly language?: "fr" | "en";
};

/**
 * `conflict` (409 PUBLIC_INTAKE_IDEMPOTENCY_CONFLICT) : même submission_id
 * réutilisé avec un payload DIFFÉRENT → collision refusée. Les autres kinds
 * réutilisent la même sémantique que le flux legacy.
 */
export type VersionedSubmissionResult =
  | { readonly kind: "received"; readonly intakeEventId: string }
  | { readonly kind: "invalid" }
  | { readonly kind: "payload" }
  | { readonly kind: "conflict" }
  | { readonly kind: "rate_limited"; readonly retryAfterSec: number | null }
  | { readonly kind: "internal" };

function dispatchVersionedResult(
  result: PublicFetchResult<PublicIntakePostSuccess>,
): VersionedSubmissionResult {
  // 201 created OU 200 replay — les deux renvoient ok:true + status:"received".
  if (
    result.envelope.ok &&
    result.envelope.data?.status === "received" &&
    (result.status === 201 || result.status === 200)
  ) {
    return { kind: "received", intakeEventId: result.envelope.data.intake_event_id };
  }
  if (!result.envelope.ok) {
    const code = result.envelope.error.code;
    if (code === "PUBLIC_INTAKE_IDEMPOTENCY_CONFLICT") return { kind: "conflict" };
    if (result.status === 200 || code === "PUBLIC_INTAKE_LINK_UNAVAILABLE") {
      return { kind: "invalid" };
    }
    if (result.status === 400 || code === "PUBLIC_INTAKE_INVALID_PAYLOAD") {
      return { kind: "payload" };
    }
    if (result.status === 429 || code === "RATE_LIMITED") {
      return { kind: "rate_limited", retryAfterSec: result.retryAfter };
    }
  }
  return { kind: "internal" };
}

export function useSubmitVersionedIntake(token: string | undefined) {
  return useMutation<VersionedSubmissionResult, Error, VersionedSubmitBody>({
    mutationFn: async (body: VersionedSubmitBody) => {
      if (!token) return { kind: "invalid" } as const;
      try {
        const result = await publicPostJson<PublicIntakePostSuccess>(buildPath(token), body);
        return dispatchVersionedResult(result);
      } catch {
        return { kind: "internal" };
      }
    },
    retry: false,
  });
}
